import React, { useState } from 'react';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import Icon from '../../../../components/AppIcon';
import { aiService } from '../../../../services/aiService';

const GeneralInfoTab = ({ productData, onFieldChange, validationErrors }) => {
  const [improvingTitle, setImprovingTitle] = useState(false);

  const handleImproveTitle = async () => {
    try {
      setImprovingTitle(true);
      
      const prompt = `Otimize este título de produto para marketplaces (máx 60 caracteres):
"${productData?.name}"

Retorne apenas o título otimizado, sem explicações.`;

      const response = await aiService?.generateContent(prompt, { maxTokens: 100 });
      
      if (response?.success) {
        const improvedTitle = response?.content?.trim()?.replace(/"/g, '');
        onFieldChange('name', improvedTitle);
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'success',
            message: 'Título otimizado com sucesso',
            progress: 100
          }
        }));
      } else {
        throw new Error(response?.error || 'IA não disponível');
      }
    } catch (error) {
      console.error('Error improving title:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: error?.message || 'Erro ao otimizar título',
          progress: 100
        }
      }));
    } finally {
      setImprovingTitle(false);
    }
  };

  const categoryOptions = [
    { value: 'Brinquedos', label: 'Brinquedos' },
    { value: 'Utilidades Domésticas', label: 'Utilidades Domésticas' },
    { value: 'Escolar', label: 'Escolar' }
  ];

  const conditionOptions = [
    { value: 'novo', label: 'Novo' },
    { value: 'usado', label: 'Usado' },
    { value: 'recondicionado', label: 'Recondicionado' }
  ];

  const estadoOptions = [
    { value: 'SP', label: 'São Paulo' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'PR', label: 'Paraná' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'BA', label: 'Bahia' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'CE', label: 'Ceará' },
    { value: 'GO', label: 'Goiás' }
  ];

  return (
    <div className="space-y-6">
      {/* Display grouped product information if applicable */}
      {productData?.metadata?.groupType && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="Grid" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-2">Produto Agrupado - {productData?.metadata?.groupType}</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Este produto contém {(productData?.metadata?.variations?.length || 0) + 1} variações agrupadas
              </p>
              
              {/* Display all SKUs */}
              {productData?.metadata?.allSkus && productData?.metadata?.allSkus?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-foreground mb-1">SKUs das Variações:</p>
                  <div className="flex flex-wrap gap-1">
                    {productData?.metadata?.allSkus?.map((sku, index) => (
                      <span key={index} className="px-2 py-1 bg-muted rounded text-xs font-mono text-foreground">
                        {sku}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display all images count */}
              {productData?.images && productData?.images?.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Image" size={14} />
                  <span>{productData?.images?.length} imagens de todas as variações</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="SKU"
          value={productData?.sku}
          disabled
          description="Gerado automaticamente"
        />
        
        <div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Nome do Produto"
                value={productData?.name}
                onChange={(e) => onFieldChange('name', e?.target?.value)}
                placeholder="Máximo 120 caracteres"
                maxLength={120}
                required
                error={validationErrors?.name}
              />
            </div>
            <Button
              onClick={handleImproveTitle}
              disabled={improvingTitle || !productData?.name}
              variant="primary"
              size="sm"
              className="mb-1"
            >
              {improvingTitle ? (
                <Icon name="Loader" size={14} className="animate-spin" />
              ) : (
                <>
                  <Icon name="Sparkles" size={14} className="mr-1" />
                  Melhorar com IA
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Marca"
          value={productData?.brand}
          onChange={(e) => onFieldChange('brand', e?.target?.value)}
          placeholder="Ex: DDG"
          required
          error={validationErrors?.brand}
        />
        
        <Select
          label="Categoria"
          options={categoryOptions}
          value={productData?.category}
          onChange={(value) => onFieldChange('category', value)}
          required
          error={validationErrors?.category}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Fornecedor"
          value={productData?.supplier}
          onChange={(e) => onFieldChange('supplier', e?.target?.value)}
          placeholder="Nome do fornecedor"
        />
        
        <Select
          label="Estado do Fornecedor"
          options={estadoOptions}
          value={productData?.supplierState}
          onChange={(value) => onFieldChange('supplierState', value)}
          placeholder="Selecione o estado"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Condição"
          options={conditionOptions}
          value={productData?.condition}
          onChange={(value) => onFieldChange('condition', value)}
        />
        
        <Input
          label="Garantia (meses)"
          type="number"
          value={productData?.warranty}
          onChange={(e) => onFieldChange('warranty', e?.target?.value)}
          placeholder="Ex: 12"
        />
      </div>

      <Input
        label="EAN / Código de Barras"
        value={productData?.ean}
        onChange={(e) => onFieldChange('ean', e?.target?.value)}
        placeholder="13 dígitos"
        maxLength={13}
      />
    </div>
  );
};

export default GeneralInfoTab;